// @ts-strict-ignore
import {inject, Injectable} from "@angular/core";
import {
	Checklist,
	CreateByNameRequest,
	IId,
	ListData,
	Markerset,
	Mediaset,
	Modules,
	Scenario,
	SimUser,
	SuccessCreateResponse,
	SuccessResponse,
	VOGSet,
	VTPSet,
} from "@models";
import {
	SimChecklistService,
	SimConfigurationService,
	SimMarkersetService,
	SimMediasetService,
	SimScenarioService,
	SimUserExtendedService,
	SimVOGSetService,
	SimVopSetService,
} from "@services";

export abstract class IScenarioLibraryService<T extends IId> {
	abstract list(
		searchTerm: string,
		searchColumn: string,
		sortColumn: string,
		sortAscending: boolean,
		page: number,
		pageSize: number,
	): Promise<ListData<T>>;

	abstract delete(id: string): Promise<SuccessResponse>;

	abstract batchDelete(ids: string[]): Promise<SuccessResponse>;

	abstract duplicate(item: IId & {name: string}): Promise<SuccessResponse>;

	abstract create(item: CreateByNameRequest): Promise<SuccessCreateResponse>;

	abstract getDefaultItem(): Promise<T>;
}

@Injectable({
	providedIn: "root",
})
export class UserManagerLibraryListService implements IScenarioLibraryService<SimUser> {
	constructor(private _service: SimUserExtendedService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, pageNumber: number, pageSize: number) {
		return this._service.fetchFilteredLocalUsers({
			sortColumn,
			sortAscending,
			pageNumber,
			pageSize,
			searchFilters: [{searchColumn, searchTerms: [searchTerm]}],
		});
	}

	delete(id: string) {
		return this._service.deleteUser({userId: id});
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatch({userIds: ids});
	}

	async duplicate(item: SimUser & {name: string}) {
		console.error("Not implemented");
		return null;
	}

	async create(item: CreateByNameRequest): Promise<SuccessCreateResponse> {
		console.error("Not implemented");
		return null;
	}

	getDefaultItem(): Promise<SimUser> {
		console.error("Not implemented");
		return null;
	}
}

@Injectable({
	providedIn: "root",
})
export class SimVopSetLibraryListService implements IScenarioLibraryService<VTPSet> {
	constructor(private _service: SimVopSetService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		return this._service.getFilteredVopSets(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createVopset(item);
	}

	delete(id: string) {
		return this._service.deleteVopSet({vopSetId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateVopSet(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchVopSet({vopSetIds: ids});
	}

	getDefaultItem() {
		return this._service.getDefaultVopSet();
	}
}

@Injectable({
	providedIn: "root",
})
export class SimVogsetLibraryListService implements IScenarioLibraryService<VOGSet> {
	constructor(private _service: SimVOGSetService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		return this._service.getFilteredVOGSets(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createVOGSet(item);
	}

	delete(id: string) {
		return this._service.deleteVOGSet({vogSetId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateVOGSet(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchVOGSet({vogSetIds: ids});
	}

	getDefaultItem() {
		return this._service.getDefaultVogSet();
	}
}

@Injectable({
	providedIn: "root",
})
export class SimMediaSetLibraryListService implements IScenarioLibraryService<Mediaset> {
	constructor(private _service: SimMediasetService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		return this._service.getFilteredMediaSets(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createMediaset(item);
	}

	delete(id: string) {
		return this._service.deleteMediaset({mediaSetId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateMediaSet(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchMediaset({mediaSetIds: ids});
	}

	getDefaultItem() {
		return this._service.getDefaultMediaSet();
	}
}

@Injectable({
	providedIn: "root",
})
export class SimChecklistLibraryListService implements IScenarioLibraryService<Checklist> {
	constructor(protected _service: SimChecklistService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		if (!SimConfigurationService.hasLicenseModule(Modules.ScenarioDesigner)) {
			return this._service.getDefaultChecklists();
		}
		return this._service.getFilteredChecklists(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createChecklist(item);
	}

	delete(id: string) {
		return this._service.deleteChecklist({checklistId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateChecklist(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchChecklist({checklistIds: ids});
	}

	getDefaultItem(): Promise<Checklist> {
		return this._service.getDefaultChecklist();
	}
}

@Injectable({
	providedIn: "root",
})
export class SimMarkersetLibraryListService implements IScenarioLibraryService<Markerset> {
	private _service = inject(SimMarkersetService);

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		return this._service.getFilteredMarkersets(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createMarkerset(item);
	}

	delete(id: string) {
		return this._service.deleteMarkerset({markersetId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateMarkerset(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchMarkerset({markersetIds: ids});
	}

	getDefaultItem() {
		return this._service.getDefaultMarkerSet();
	}
}

@Injectable({
	providedIn: "root",
})
export class SimScenarioLibraryListService implements IScenarioLibraryService<Scenario> {
	constructor(private _service: SimScenarioService) {}

	list(searchTerm: string, searchColumn: string, sortColumn: string, sortAscending: boolean, page: number, pageSize: number) {
		return this._service.getFilteredScenarios(searchTerm, searchColumn, sortColumn, sortAscending, page, pageSize);
	}

	create(item: CreateByNameRequest) {
		return this._service.createScenario(item);
	}

	delete(id: string) {
		return this._service.deleteScenario({scenarioId: id});
	}

	duplicate(item: IId & {name: string}) {
		return this._service.duplicateScenario(item);
	}

	batchDelete(ids: string[]) {
		return this._service.deleteBatchScenario({scenarioIds: ids});
	}

	getDefaultItem() {
		return this._service.getDefaultScenario();
	}
}
